import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ANALYTICS_TIME_ZONE } from "../shared/analytics";
import type { CollectorStatus } from "../shared/analytics-collector";
import type {
  AnalyticsGranularity,
  AnalyticsGroup,
  AnalyticsQuery,
  AnalyticsReport,
  AnalyticsTotals,
} from "../shared/analytics";
import { ApiError, request } from "./api";
import { TransferAnalytics } from "./TransferAnalytics";
import "./analytics.css";

type AnalyticsResponse = AnalyticsReport & {
  collector: CollectorStatus;
  observationCaptureHealthy?: boolean;
};
type Filter = Pick<AnalyticsQuery, "startDate" | "endDate" | "granularity"> & {
  agentId: string;
  group: AnalyticsGroup | "";
};
type Preset = "today" | "last7" | "thisMonth" | "lastMonth" | "custom";
type DetailTab = "calls" | "activity";
const PAGE_SIZE = 50;
const TIME_ZONE = ANALYTICS_TIME_ZONE;
const safeId = /^[A-Za-z0-9_-]{1,100}$/;

export function companyDate(timestamp: number): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(timestamp);
  const part = (type: string) =>
    parts.find((item) => item.type === type)?.value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}
const dateNumber = (value: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return NaN;
  const [year, month, day] = value.split("-").map(Number);
  const time = Date.UTC(year, month - 1, day);
  return new Date(time).toISOString().slice(0, 10) === value ? time : NaN;
};
const shiftDate = (value: string, days: number) =>
  new Date(dateNumber(value) + days * 86_400_000).toISOString().slice(0, 10);
const monthStart = (value: string) => `${value.slice(0, 7)}-01`;
const presetRange = (preset: Exclude<Preset, "custom">, today: string) => {
  if (preset === "today") return { startDate: today, endDate: today };
  if (preset === "last7")
    return { startDate: shiftDate(today, -6), endDate: today };
  if (preset === "thisMonth")
    return { startDate: monthStart(today), endDate: today };
  const endDate = shiftDate(monthStart(today), -1);
  return { startDate: monthStart(endDate), endDate };
};
export function validateAnalyticsDates(
  startDate: string,
  endDate: string,
  today: string,
): string | null {
  const start = dateNumber(startDate);
  const end = dateNumber(endDate);
  if (!Number.isFinite(start) || !Number.isFinite(end))
    return "Enter valid From and To dates.";
  if (start > end) return "From must be on or before To.";
  if (end > dateNumber(today)) return "The range cannot include future dates.";
  if ((end - start) / 86_400_000 > 365)
    return "Choose at most 366 days, including both dates.";
  return null;
}
const formatStamp = (value: number | null) =>
  value === null
    ? "Not yet"
    : new Intl.DateTimeFormat("en-US", {
        timeZone: TIME_ZONE,
        dateStyle: "medium",
        timeStyle: "short",
      }).format(value);
const formatDuration = (seconds: number) => {
  const wholeSeconds = Math.round(Math.max(0, seconds));
  const minutes = Math.floor(wholeSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours) return `${hours}h ${remainder}m`;
  if (minutes) return `${minutes}m ${wholeSeconds % 60}s`;
  return `${wholeSeconds}s`;
};
const formatObserved = (ms: number) => formatDuration(ms / 1000);
const awayMs = (totals: AnalyticsTotals) =>
  totals.statusMs.break +
  totals.statusMs.lunch +
  totals.statusMs.training +
  totals.statusMs.meeting;
const detailLabel = (value: string) =>
  value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const contactUrl = (locationId: string, contactId: string | null) =>
  contactId && safeId.test(locationId) && safeId.test(contactId)
    ? `https://app.gohighlevel.com/v2/location/${locationId}/contacts/detail/${contactId}`
    : null;

export function Analytics() {
  const today = companyDate(Date.now());
  const [preset, setPreset] = useState<Preset>("today");
  const [draft, setDraft] = useState<Filter>({
    ...presetRange("today", today),
    granularity: "day",
    agentId: "",
    group: "",
  });
  const [applied, setApplied] = useState<Filter>(draft);
  const [dateError, setDateError] = useState<string | null>(null);
  const [report, setReport] = useState<AnalyticsResponse | null>(null);
  const [allAgents, setAllAgents] = useState<
    AnalyticsReport["availableAgents"]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [reload, setReload] = useState(0);
  const lastQuery = useRef<string | null>(null);
  const [detailTab, setDetailTab] = useState<DetailTab>("calls");
  const [callOffset, setCallOffset] = useState(0);
  const [intervalOffset, setIntervalOffset] = useState(0);
  const [activityOffset, setActivityOffset] = useState(0);

  const query = useMemo(() => {
    const params = new URLSearchParams({
      startDate: applied.startDate,
      endDate: applied.endDate,
      granularity: applied.granularity,
    });
    if (applied.agentId) params.set("agentId", applied.agentId);
    if (applied.group) params.set("group", applied.group);
    if (callOffset) params.set("callOffset", String(callOffset));
    if (intervalOffset) params.set("intervalOffset", String(intervalOffset));
    if (activityOffset) params.set("activityOffset", String(activityOffset));
    return params.toString();
  }, [applied, callOffset, intervalOffset, activityOffset]);
  const transferQuery = useMemo(() => {
    const params = new URLSearchParams({
      startDate: applied.startDate,
      endDate: applied.endDate,
      granularity: applied.granularity,
    });
    if (applied.agentId) params.set("agentId", applied.agentId);
    if (applied.group) params.set("group", applied.group);
    return params.toString();
  }, [applied]);
  const loseAccess = useCallback(() => {
    setReport(null);
    setAllAgents([]);
    setError("Analytics access is unavailable for this session.");
    setForbidden(true);
  }, []);

  useEffect(() => {
    if (forbidden) return;
    let current = true;
    let inFlight = false;
    let timer: number | undefined;
    setLoading(true);
    setError(null);
    if (lastQuery.current !== query) setReport(null);
    lastQuery.current = query;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      let delay = 30_000;
      try {
        const next = await request<AnalyticsResponse>(
          "GET",
          `/api/admin/analytics?${query}`,
        );
        if (!current) return;
        setReport(next);
        setAllAgents(next.availableAgents);
        setError(null);
        delay = next.collector.state === "running" ? 10_000 : 30_000;
      } catch (cause) {
        if (!current) return;
        if (cause instanceof ApiError && [401, 403].includes(cause.status)) {
          setReport(null);
          setAllAgents([]);
          setError("Analytics access is unavailable for this session.");
          setForbidden(true);
          setLoading(false);
          current = false;
        } else {
          setError(
            cause instanceof Error
              ? cause.message
              : "Could not load analytics.",
          );
        }
      } finally {
        inFlight = false;
        if (current) {
          setLoading(false);
          if (document.visibilityState !== "hidden")
            timer = window.setTimeout(() => void load(), delay);
        }
      }
    };
    const onVisibility = () => {
      window.clearTimeout(timer);
      if (document.visibilityState === "visible") void load();
    };
    document.addEventListener("visibilitychange", onVisibility);
    if (document.visibilityState !== "hidden") void load();
    return () => {
      current = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearTimeout(timer);
    };
  }, [query, reload, forbidden]);

  const resetPages = () => {
    setCallOffset(0);
    setIntervalOffset(0);
    setActivityOffset(0);
  };
  const apply = () => {
    const validation = validateAnalyticsDates(
      draft.startDate,
      draft.endDate,
      companyDate(Date.now()),
    );
    setDateError(validation);
    if (validation) return;
    resetPages();
    setApplied({ ...draft });
  };
  const selectAgent = (agentId: string) => {
    resetPages();
    setDetailTab("calls");
    setDraft((previous) => ({ ...previous, agentId, group: applied.group }));
    setApplied((previous) => ({ ...previous, agentId }));
  };
  const selectBucket = (startAt: number, endAt: number) => {
    const range = {
      startDate: companyDate(startAt),
      endDate: companyDate(endAt - 1),
      granularity: "day" as AnalyticsGranularity,
    };
    resetPages();
    setPreset("custom");
    setDateError(null);
    setDraft((previous) => ({ ...previous, ...range }));
    setApplied((previous) => ({ ...previous, ...range }));
  };
  const refreshHistory = async () => {
    if (
      syncBusy ||
      !report ||
      report.collector.state === "running" ||
      report.collector.state === "unconfigured" ||
      (report.collector.nextRunAt !== null &&
        report.collector.nextRunAt > Date.now())
    )
      return;
    setSyncBusy(true);
    try {
      await request<CollectorStatus>("POST", "/api/admin/analytics/sync");
      setReload((value) => value + 1);
    } catch (cause) {
      if (cause instanceof ApiError && [401, 403].includes(cause.status)) {
        setReport(null);
        setAllAgents([]);
        setError("Analytics access is unavailable for this session.");
        setForbidden(true);
      } else {
        setError(
          cause instanceof Error
            ? cause.message
            : "Could not request the scheduled collection.",
        );
      }
    } finally {
      setSyncBusy(false);
    }
  };

  const range = report?.range;
  const totals = report?.overall;
  const hasObservationHistory =
    report?.observationStartedAt !== null &&
    report?.observationStartedAt !== undefined &&
    range !== undefined &&
    report.observationStartedAt < range.endAt;
  const observedBy = (endAt: number) =>
    report?.observationStartedAt !== null &&
    report?.observationStartedAt !== undefined &&
    report.observationStartedAt < endAt;
  const agents = allAgents.length ? allAgents : (report?.agents ?? []);
  const agentOptions = agents.filter(
    (agent) =>
      !draft.group ||
      (draft.group === "closer"
        ? agent.duty === "closer"
        : agent.bdrMember === true),
  );
  const selectedAgent = agents.find((agent) => agent.id === applied.agentId);
  const selected = report?.selectedAgent;
  const maxCalls = Math.max(
    1,
    ...(report?.buckets.map((bucket) => bucket.totals.callCount) ?? [0]),
  );

  return (
    <div className="analytics-page">
      <div className="page-heading analytics-heading">
        <div>
          <p className="eyebrow">DISPATCH / ANALYTICS</p>
          <h1>Team analytics</h1>
          <p className="muted">
            GHL call records and observed activity, grouped by Las Vegas time
            (PST/PDT).
          </p>
        </div>
      </div>

      <form
        className="analytics-filters"
        onSubmit={(event) => {
          event.preventDefault();
          apply();
        }}
      >
        <div className="analytics-presets" aria-label="Date presets">
          {(
            [
              ["today", "Today"],
              ["last7", "Last 7 days"],
              ["thisMonth", "This month"],
              ["lastMonth", "Last month"],
            ] as const
          ).map(([value, text]) => (
            <button
              key={value}
              type="button"
              className={
                preset === value
                  ? "analytics-preset active"
                  : "analytics-preset"
              }
              aria-pressed={preset === value}
              onClick={() => {
                setPreset(value);
                setDraft((previous) => ({
                  ...previous,
                  ...presetRange(value, companyDate(Date.now())),
                }));
                setDateError(null);
              }}
            >
              {text}
            </button>
          ))}
        </div>
        <div className="analytics-filter-fields">
          <label>
            <span>From</span>
            <input
              type="date"
              value={draft.startDate}
              max={today}
              onChange={(event) => {
                setPreset("custom");
                setDraft((previous) => ({
                  ...previous,
                  startDate: event.target.value,
                }));
              }}
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="date"
              value={draft.endDate}
              max={today}
              onChange={(event) => {
                setPreset("custom");
                setDraft((previous) => ({
                  ...previous,
                  endDate: event.target.value,
                }));
              }}
            />
          </label>
          <label>
            <span>Group by</span>
            <select
              value={draft.granularity}
              onChange={(event) =>
                setDraft((previous) => ({
                  ...previous,
                  granularity: event.target.value as AnalyticsGranularity,
                }))
              }
            >
              <option value="day">Day</option>
              <option value="month">Month</option>
            </select>
          </label>
          <label>
            <span>Group</span>
            <select
              value={draft.group}
              onChange={(event) => {
                const group = event.target.value as Filter["group"];
                setDraft((previous) => ({
                  ...previous,
                  group,
                  agentId:
                    !group ||
                    agents.some(
                      (agent) =>
                        agent.id === previous.agentId &&
                        (group === "closer"
                          ? agent.duty === "closer"
                          : agent.bdrMember === true),
                    )
                      ? previous.agentId
                      : "",
                }));
              }}
            >
              <option value="">All agents</option>
              <option value="closer">Closers</option>
              <option value="bdr">BDR</option>
            </select>
          </label>
          <label>
            <span>Agent</span>
            <select
              value={draft.agentId}
              onChange={(event) =>
                setDraft((previous) => ({
                  ...previous,
                  agentId: event.target.value,
                }))
              }
            >
              <option value="">All agents</option>
              {agentOptions.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name}
                </option>
              ))}
            </select>
          </label>
          <button type="submit">Apply filters</button>
          <button
            type="button"
            className="secondary"
            onClick={() => {
              const next = {
                ...presetRange("today", companyDate(Date.now())),
                granularity: "day" as AnalyticsGranularity,
                agentId: "",
                group: "" as const,
              };
              setPreset("today");
              setDraft(next);
              setDateError(null);
              resetPages();
              setApplied(next);
            }}
          >
            Reset
          </button>
        </div>
        {dateError && (
          <p className="analytics-validation" role="alert">
            {dateError}
          </p>
        )}
      </form>

      {error && (
        <div className="analytics-error" role="alert">
          {error}
        </div>
      )}
      {loading && !report && (
        <p className="analytics-loading" role="status">
          Loading analytics…
        </p>
      )}
      {report && totals && (
        <>
          <div className="analytics-coverage" role="status">
            <div>
              <strong>Cached call records</strong>
              <span>
                {report.coverage.complete &&
                report.coverage.coveredThroughAt !== null
                  ? "Previously collected records cover this selected range."
                  : "Collected records may cover only part of this selected range."}
              </span>
              <span>
                Last collected for this range:{" "}
                {formatStamp(report.coverage.syncedAt)} PT
              </span>
              {report.coverage.coveredThroughAt !== null ? (
                <span>
                  Collected through:{" "}
                  {formatStamp(report.coverage.coveredThroughAt)} PT
                </span>
              ) : (
                <span>Call record coverage is unverified.</span>
              )}
              {report.coverage.unknown &&
                report.coverage.coveredThroughAt !== null && (
                  <span>Coverage is unverified.</span>
                )}
              {report.coverage.unattributedCalls > 0 && (
                <span>
                  {report.coverage.unattributedCalls} GHL records have no
                  recorded agent and are excluded from agent totals.
                </span>
              )}
            </div>
            <div>
              <strong>Observed activity</strong>
              <span>
                {report.observationStartedAt === null
                  ? "No observation history yet"
                  : `Tracked since ${formatStamp(report.observationStartedAt)} PT`}
              </span>
              <span>Earlier activity is not available.</span>
              {report.observationCaptureHealthy === false && (
                <span>Live observation capture is unavailable.</span>
              )}
            </div>
            <div>
              <strong>
                Global collection: {detailLabel(report.collector.state)}
              </strong>
              <span>
                Scheduled every 3 hours. Opening or filtering reports reads
                cached results.
              </span>
              <span>
                Last successful collection:{" "}
                {formatStamp(report.collector.lastSuccessfulAt)} PT
              </span>
              <span>
                Next scheduled collection:{" "}
                {formatStamp(report.collector.nextRunAt)} PT
              </span>
              {report.collector.state === "running" && (
                <span>
                  Collecting{" "}
                  {report.collector.phase === "transfers"
                    ? "contact signals"
                    : "calls"}{" "}
                  now.
                </span>
              )}
              {report.collector.state === "partial" && (
                <span>
                  Last collection was partial. Cached results remain available.
                </span>
              )}
              {report.collector.state === "failed" && (
                <span>
                  Last collection failed. Cached results remain available.
                </span>
              )}
              {report.collector.state === "unconfigured" && (
                <span>
                  Collection is unavailable because GHL access is not
                  configured.
                </span>
              )}
            </div>
            <div className="analytics-coverage-actions">
              <button
                type="button"
                className="secondary"
                onClick={() => setReload((value) => value + 1)}
              >
                Refresh results
              </button>
              <button
                type="button"
                className="secondary"
                onClick={() => void refreshHistory()}
                disabled={
                  syncBusy ||
                  report.collector.state === "running" ||
                  report.collector.state === "unconfigured" ||
                  (report.collector.nextRunAt !== null &&
                    report.collector.nextRunAt > Date.now())
                }
              >
                {syncBusy ? "Requesting…" : "Sync latest activity"}
              </button>
            </div>
          </div>

          <div className="analytics-metrics">
            <div className="analytics-metric">
              <span>GHL recorded calls</span>
              <strong>{totals.callCount.toLocaleString()}</strong>
              <small>
                {report.coverage.complete &&
                report.coverage.coveredThroughAt !== null
                  ? "Cached records in selected range"
                  : "Cached records so far; coverage may be partial"}
              </small>
            </div>
            <div className="analytics-metric">
              <span>GHL recorded duration</span>
              <strong>{formatDuration(totals.recordedDurationSeconds)}</strong>
              <small>
                {totals.unknownDurationCalls} calls have unknown duration
              </small>
            </div>
            <div className="analytics-metric">
              <span>Observed call-session time</span>
              <strong>
                {hasObservationHistory
                  ? formatObserved(totals.statusMs.busy)
                  : "Not tracked"}
              </strong>
              <small>Busy includes ringing and retained signal</small>
            </div>
            <div className="analytics-metric">
              <span>Observed available time</span>
              <strong>
                {hasObservationHistory
                  ? formatObserved(totals.statusMs.available)
                  : "Not tracked"}
              </strong>
              <small>From GHL Connect observations</small>
            </div>
          </div>

          <TransferAnalytics
            key={transferQuery}
            query={transferQuery}
            reload={reload}
            onAuthLost={loseAccess}
          />

          <section
            className="analytics-panel"
            aria-labelledby="analytics-periods-heading"
          >
            <div className="analytics-panel-heading">
              <div>
                <p className="eyebrow">WHEN</p>
                <h2 id="analytics-periods-heading">Period trend</h2>
              </div>
              <span>{report.buckets.length} periods</span>
            </div>
            {report.buckets.length ? (
              <>
                <div
                  className="analytics-chart"
                  role="group"
                  aria-label="Recorded calls by period. Select a bar to narrow the date range."
                >
                  {report.buckets.map((bucket) => (
                    <button
                      key={bucket.key}
                      type="button"
                      className="analytics-bar-button"
                      onClick={() => selectBucket(bucket.startAt, bucket.endAt)}
                      aria-label={`${bucket.key}: ${bucket.totals.callCount} recorded calls. Narrow range.`}
                    >
                      <span className="analytics-bar-value">
                        {bucket.totals.callCount}
                      </span>
                      <span className="analytics-bar-track">
                        <span
                          style={{
                            height: `${Math.max(4, (bucket.totals.callCount / maxCalls) * 100)}%`,
                          }}
                        />
                      </span>
                      <span className="analytics-bar-label">{bucket.key}</span>
                    </button>
                  ))}
                </div>
                <div className="analytics-table-scroll">
                  <table>
                    <caption className="sr-only">Period analytics</caption>
                    <thead>
                      <tr>
                        <th scope="col">Period</th>
                        <th scope="col">Recorded calls</th>
                        <th scope="col">Recorded duration</th>
                        <th scope="col">Observed available</th>
                        <th scope="col">Observed call session</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.buckets.map((bucket) => (
                        <tr key={bucket.key}>
                          <th scope="row">
                            <button
                              type="button"
                              className="analytics-link-button"
                              onClick={() =>
                                selectBucket(bucket.startAt, bucket.endAt)
                              }
                            >
                              {bucket.key}
                            </button>
                          </th>
                          <td>{bucket.totals.callCount}</td>
                          <td>
                            {formatDuration(
                              bucket.totals.recordedDurationSeconds,
                            )}
                          </td>
                          <td>
                            {observedBy(bucket.endAt)
                              ? formatObserved(bucket.totals.statusMs.available)
                              : "Not tracked"}
                          </td>
                          <td>
                            {observedBy(bucket.endAt)
                              ? formatObserved(bucket.totals.statusMs.busy)
                              : "Not tracked"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="analytics-empty">
                No periods returned for this range.
              </p>
            )}
          </section>

          <section
            className="analytics-panel"
            aria-labelledby="analytics-agents-heading"
          >
            <div className="analytics-panel-heading">
              <div>
                <p className="eyebrow">WHO</p>
                <h2 id="analytics-agents-heading">Agents</h2>
              </div>
              <span>{report.agents.length} shown</span>
            </div>
            {report.agents.length ? (
              <div className="analytics-table-scroll">
                <table>
                  <caption className="sr-only">Agent analytics</caption>
                  <thead>
                    <tr>
                      <th scope="col">Agent</th>
                      <th scope="col">Calls</th>
                      <th scope="col">Recorded duration</th>
                      <th scope="col">Available</th>
                      <th scope="col">Call session</th>
                      <th scope="col">Away</th>
                      <th scope="col">Unknown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.agents.map((agent) => (
                      <tr
                        key={agent.id}
                        className={
                          applied.agentId === agent.id
                            ? "analytics-selected-row"
                            : ""
                        }
                      >
                        <th scope="row">
                          <button
                            type="button"
                            className="analytics-link-button"
                            onClick={() => selectAgent(agent.id)}
                          >
                            {agent.name}
                          </button>
                        </th>
                        <td>{agent.totals.callCount}</td>
                        <td>
                          {formatDuration(agent.totals.recordedDurationSeconds)}
                        </td>
                        <td>
                          {hasObservationHistory
                            ? formatObserved(agent.totals.statusMs.available)
                            : "Not tracked"}
                        </td>
                        <td>
                          {hasObservationHistory
                            ? formatObserved(agent.totals.statusMs.busy)
                            : "Not tracked"}
                        </td>
                        <td>
                          {hasObservationHistory
                            ? formatObserved(awayMs(agent.totals))
                            : "Not tracked"}
                        </td>
                        <td>
                          {hasObservationHistory
                            ? formatObserved(agent.totals.statusMs.unknown)
                            : "Not tracked"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="analytics-empty">No agents in this report.</p>
            )}
          </section>

          {applied.agentId && (
            <section
              className="analytics-panel"
              aria-labelledby="analytics-detail-heading"
            >
              <div className="analytics-panel-heading">
                <div>
                  <p className="eyebrow">WHAT / WHEN</p>
                  <h2 id="analytics-detail-heading">
                    {selectedAgent?.name ?? "Selected agent"}
                  </h2>
                </div>
                <button
                  type="button"
                  className="secondary"
                  onClick={() => selectAgent("")}
                >
                  All agents
                </button>
              </div>
              <div
                className="analytics-tabs"
                role="group"
                aria-label="Agent details"
              >
                <button
                  type="button"
                  aria-pressed={detailTab === "calls"}
                  className={detailTab === "calls" ? "active" : ""}
                  onClick={() => setDetailTab("calls")}
                >
                  Calls
                </button>
                <button
                  type="button"
                  aria-pressed={detailTab === "activity"}
                  className={detailTab === "activity" ? "active" : ""}
                  onClick={() => setDetailTab("activity")}
                >
                  Activity
                </button>
              </div>
              {!selected ? (
                <p className="analytics-empty">
                  No detail rows are available for this agent.
                </p>
              ) : detailTab === "calls" ? (
                <div className="analytics-detail-panel">
                  <p className="analytics-note">
                    GHL recorded duration is separate from observed call-session
                    time. It does not prove talk time.
                  </p>
                  {selected.calls.items.length ? (
                    <div className="analytics-table-scroll">
                      <table>
                        <caption className="sr-only">
                          Recorded calls for{" "}
                          {selectedAgent?.name ?? "selected agent"}
                        </caption>
                        <thead>
                          <tr>
                            <th scope="col">Started (PT)</th>
                            <th scope="col">Direction</th>
                            <th scope="col">Status</th>
                            <th scope="col">Duration</th>
                            <th scope="col">Contact</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selected.calls.items.map((call) => {
                            const url = contactUrl(
                              call.locationId,
                              call.contactId,
                            );
                            return (
                              <tr key={`${call.locationId}:${call.messageId}`}>
                                <td>{formatStamp(call.startedAt)}</td>
                                <td>{detailLabel(call.direction)}</td>
                                <td>{detailLabel(call.status)}</td>
                                <td>
                                  {call.durationSeconds === null
                                    ? "Unknown"
                                    : formatDuration(call.durationSeconds)}
                                </td>
                                <td>
                                  {url ? (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Open contact
                                    </a>
                                  ) : (
                                    "Unavailable"
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="analytics-empty">
                      No recorded calls in this range.
                    </p>
                  )}
                  <div className="analytics-pagination">
                    <button
                      type="button"
                      className="secondary"
                      disabled={callOffset === 0}
                      onClick={() =>
                        setCallOffset(Math.max(0, callOffset - PAGE_SIZE))
                      }
                    >
                      Previous calls
                    </button>
                    <span>
                      Rows {callOffset + 1}–
                      {callOffset + selected.calls.items.length}
                    </span>
                    <button
                      type="button"
                      className="secondary"
                      disabled={selected.calls.nextOffset === null}
                      onClick={() => setCallOffset(selected.calls.nextOffset!)}
                    >
                      Next calls
                    </button>
                  </div>
                </div>
              ) : (
                <div className="analytics-detail-panel">
                  <p className="analytics-note">
                    Activity durations come from observed status intervals.
                    Earlier history is unavailable.
                  </p>
                  <h3>Status intervals</h3>
                  {selected.intervals.items.length ? (
                    <div className="analytics-table-scroll">
                      <table>
                        <caption className="sr-only">
                          Observed status intervals
                        </caption>
                        <thead>
                          <tr>
                            <th scope="col">State</th>
                            <th scope="col">Start (PT)</th>
                            <th scope="col">End (PT)</th>
                            <th scope="col">Duration</th>
                            <th scope="col">Evidence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selected.intervals.items.map((interval, index) => (
                            <tr
                              key={`${interval.startAt}:${interval.status}:${index}`}
                            >
                              <th scope="row">
                                {detailLabel(interval.status)}
                              </th>
                              <td>{formatStamp(interval.startAt)}</td>
                              <td>{formatStamp(interval.endAt)}</td>
                              <td>
                                {formatObserved(
                                  interval.endAt - interval.startAt,
                                )}
                              </td>
                              <td>
                                {detailLabel(interval.evidenceHealth)} ·{" "}
                                {detailLabel(interval.evidenceReason)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="analytics-empty">
                      No observed intervals in this range.
                    </p>
                  )}
                  <div className="analytics-pagination">
                    <button
                      type="button"
                      className="secondary"
                      disabled={intervalOffset === 0}
                      onClick={() =>
                        setIntervalOffset(
                          Math.max(0, intervalOffset - PAGE_SIZE),
                        )
                      }
                    >
                      Previous intervals
                    </button>
                    <span>
                      Rows {intervalOffset + 1}–
                      {intervalOffset + selected.intervals.items.length}
                    </span>
                    <button
                      type="button"
                      className="secondary"
                      disabled={selected.intervals.nextOffset === null}
                      onClick={() =>
                        setIntervalOffset(selected.intervals.nextOffset!)
                      }
                    >
                      Next intervals
                    </button>
                  </div>
                  <h3>Recorded actions</h3>
                  {selected.actions.items.length ? (
                    <div className="analytics-table-scroll">
                      <table>
                        <caption className="sr-only">Recorded actions</caption>
                        <thead>
                          <tr>
                            <th scope="col">When (PT)</th>
                            <th scope="col">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selected.actions.items.map((action) => (
                            <tr key={action.id}>
                              <td>{formatStamp(action.createdAt)}</td>
                              <th scope="row">{detailLabel(action.type)}</th>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="analytics-empty">
                      No recorded actions in this range.
                    </p>
                  )}
                  <div className="analytics-pagination">
                    <button
                      type="button"
                      className="secondary"
                      disabled={activityOffset === 0}
                      onClick={() =>
                        setActivityOffset(
                          Math.max(0, activityOffset - PAGE_SIZE),
                        )
                      }
                    >
                      Previous actions
                    </button>
                    <span>
                      Rows {activityOffset + 1}–
                      {activityOffset + selected.actions.items.length}
                    </span>
                    <button
                      type="button"
                      className="secondary"
                      disabled={selected.actions.nextOffset === null}
                      onClick={() =>
                        setActivityOffset(selected.actions.nextOffset!)
                      }
                    >
                      Next actions
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}
